import {
  VictoryAxis, VictoryLine, VictoryChart, VictoryLabel,
  VictoryVoronoiContainer,
  VictoryTheme,
  VictoryGroup,
  VictoryTooltip,
  VictoryArea,
  VictoryScatter,
} from 'victory';
import useContainerSize from '../../hooks/useContainerSize';
import { formatSnapshot } from '../../utils/formatters';
import { graphColourMap } from '../../utils/graphColours';
import { MultiLineData } from '../../types/global';
import { AreaGradient } from '../../shared/components';

interface LineGraphProps {
  graphData: MultiLineData[];
  title?: string
  categoricalLabel?: string
  valueLabel?: string
  showValueAxis: boolean;
  showCategoricalAxis: boolean;
  gradient: boolean
}

export default function MultiLineGraph({
  graphData, title, categoricalLabel, valueLabel, showValueAxis, showCategoricalAxis, gradient,
}: LineGraphProps) {
  const [containerRef, { width: rawWidth, height }] = useContainerSize<HTMLDivElement>();
  const dimensions = { width: rawWidth - 10, height };
  if (graphData.length === 0) {
    return <div>No data available</div>;
  }
  const formattedGraphData = graphData.map((series) => ({
    ...series,
    data: series.data.map((point) => ({
      x: formatSnapshot(point.group.toString(), 'short'),
      y: point.value,
    })).filter((point) => point.y !== null && point.y !== undefined), // Filter out none points so that tooltips don't show for them
  }));
  const allXValues = graphData.flatMap((series) => (series.data.map((point) => formatSnapshot(point.group.toString(), 'short')))).flat();
  const uniqueXValues = [...new Set(allXValues)];
  return (
    <div ref={containerRef} className="w-full h-full">
      {dimensions.width > 0 && (
      <VictoryChart
        width={dimensions.width}
        height={dimensions.height}
        domain={{ x: [1, uniqueXValues.length] }}
        categories={{ x: uniqueXValues }}
        domainPadding={{ x: 20, y: 10 }}
        padding={{
          top: 10, bottom: 30, left: 10, right: 10,
        }} // adjust as needed
        containerComponent={(
          <VictoryVoronoiContainer
            responsive={false}
          />
      )}
        theme={VictoryTheme.clean}
      >
        {title ? (
          <VictoryLabel
            text={title}
            style={{
              ...VictoryTheme.clean.label,
              fontSize: 10,
            }}
            dx={28}
            dy={18}
          />
        ) : ''}

        <VictoryAxis
          tickValues={allXValues}
          tickLabelComponent={<VictoryLabel angle={-15} />}
          style={{
            axis: { stroke: showCategoricalAxis ? '#000' : 'transparent' },
            ticks: { stroke: 'transparent' },
            tickLabels: { fill: showCategoricalAxis ? '#000' : 'transparent', fontSize: 8 },
          }}
        />

        <VictoryAxis
          dependentAxis
          label={valueLabel || ''}
          tickFormat={(x: number) => {
            if (x >= 1000) {
              const value = x / 1000;
              return Number.isInteger(value) ? `${value}K` : `${value.toFixed(1)}K`;
            }
            return Math.round(x).toString();
          }}
          style={{
            axis: { stroke: 'transparent' },
            axisLabel: {
              fontSize: 8,
              padding: 50,
              fill: showValueAxis ? '#000' : 'transparent',
            },
            tickLabels: {
              fontSize: 8,
              fill: showValueAxis ? '#000' : 'transparent',
            },
            grid: { stroke: '#d9d9d9' },
          }}
        />
        {
          categoricalLabel ? (<VictoryLabel text={categoricalLabel} dy={290} dx={160} style={{ fontSize: 14 }} />
          ) : ''
        }
        {formattedGraphData.map((s, i) => {
          const color = VictoryTheme.clean.palette?.qualitative?.[i] || '#000000';
          const colour = graphColourMap[s.name as keyof typeof graphColourMap] || color;
          const toolTip = (index: number) => (
            <VictoryTooltip
              style={{ fontSize: 10, borderRadius: 50 }}
              flyoutStyle={{
                fill: 'white',
                stroke: 'transparent',
                border: 'none',
                opacity: 0.5,
              }}
              centerOffset={{ x: 30 * index, y: 30 * index }}
              cornerRadius={8}
              constrainToVisibleArea
            />
          );
          return (
            <VictoryGroup
              key={s.name}
              data={s.data}
              labels={({ datum }) => {
                const { y } = datum as { x: string; y: number | null | undefined };
                return y == null ? null : `${y} ${s.name}`;
              }}
              labelComponent={toolTip(i)}
            >

              {gradient && (
              <VictoryArea
                interpolation="monotoneX"
                style={{
                  data: {
                    fill: `url(#areaGradient-${i})`,
                    fillOpacity: 1,
                  },
                }}
              />
              )}
              <VictoryLine
                interpolation="monotoneX"
                style={{
                  data: {
                    stroke: colour,
                    strokeWidth: 2,
                  },
                }}
              />
              <VictoryScatter
                size={({ active }) => (active ? 4 : 0)}
                style={{
                  data: {
                    fill: 'white',
                    stroke: colour,
                    strokeWidth: 2,
                    textShadow: '0 0 3px #000000',
                    filter: 'drop-shadow(0 0 8px rgba(196, 58, 49, 0.8))',
                  },
                }}
              />
            </VictoryGroup>
          );
        })}

        <VictoryLabel
          className="text-xs"
          text={categoricalLabel}
          dx={30}
          dy={285}
        />
        {gradient && formattedGraphData.map((s, i) => {
          const color = VictoryTheme.clean.palette?.qualitative?.[i] || '#000000';
          const colour = graphColourMap[s.name as keyof typeof graphColourMap] || color;
          return (
            <AreaGradient
              key={`gradient-${s.name}`}
              id={`areaGradient-${i}`}
              colour={colour}
              startOpacity={0.1}
              endOpacity={0.03}
            />
          );
        })}
      </VictoryChart>
      )}
    </div>
  );
}
